// @hypneuronerd/storage
//
// Tiny localStorage helper with auto-namespacing under `hypneuronerd:`,
// JSON serialization, and graceful failure when storage is disabled.

const NAMESPACE = 'hypneuronerd:';

function namespacedKey(key: string): string {
  return key.startsWith(NAMESPACE) ? key : NAMESPACE + key;
}

function ls(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const probe = '__hnn_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    return null;
  }
}

export const storage = {
  /** Read a value. Returns `fallback` if missing, unparseable, or storage is unavailable. */
  get<T>(key: string, fallback: T): T {
    const store = ls();
    if (!store) return fallback;
    const raw = store.getItem(namespacedKey(key));
    if (raw === null) return fallback;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  },

  /** Write a value (JSON-serialized). No-op if storage is unavailable or quota is exceeded. */
  set<T>(key: string, value: T): void {
    const store = ls();
    if (!store) return;
    try {
      store.setItem(namespacedKey(key), JSON.stringify(value));
    } catch {
      // QuotaExceededError or serialization failure — silently drop.
    }
  },

  /** Remove a single key. */
  remove(key: string): void {
    const store = ls();
    if (!store) return;
    try {
      store.removeItem(namespacedKey(key));
    } catch {
      // ignore
    }
  },

  /** Remove every key whose namespaced form starts with `prefix`. Pass '' to wipe all `hypneuronerd:*` keys. */
  clearNamespace(prefix: string = ''): void {
    const store = ls();
    if (!store) return;
    const full = namespacedKey(prefix);
    const victims: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      if (k && k.startsWith(full)) victims.push(k);
    }
    for (const k of victims) {
      try {
        store.removeItem(k);
      } catch {
        // ignore
      }
    }
  },

  /** True if localStorage is reachable and writable. */
  isAvailable(): boolean {
    return ls() !== null;
  },
};

export type StorageAPI = typeof storage;
